import React, { useRef, useEffect, useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const ChatInterface = ({
  messages,
  loading,
  streaming,
  loadingMessage,
  userInput,
  setUserInput,
  handleSend,
  hasTyped,
  setHasTyped,
}) => {
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [screenSize, setScreenSize] = useState('default');
  
  // Check screen resolution
  useEffect(() => {
    const checkResolution = () => {
      if (window.innerWidth >= 2560) {
        // 1440p monitor (2560x1440)
        setScreenSize('1440p');
      } else if (window.innerWidth >= 1920) {
        // 1080p monitor (1920x1080)
        setScreenSize('1080p');
      } else if (window.innerWidth >= 1280) {
        // 720p monitor (1280x720)
        setScreenSize('720p');
      } else {
        // Smaller resolutions (mobile)
        setScreenSize('mobile');
      }
    };
    
    // Check on initial load
    checkResolution();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkResolution);
    
    // Clean up
    return () => window.removeEventListener('resize', checkResolution);
  }, []);
  
  // Auto-scroll to bottom when messages change (but not on initial mount)
  useEffect(() => {
    // Only auto-scroll if there are messages, preventing scroll on page load
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, streaming]);

  return (
    <div className="flex flex-col w-full bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-lg border border-orange-200/50 dark:border-blue-700/30">
      {/* Scrollable Messages - Compact height, larger on 1440p */}
      <ScrollArea className="overflow-y-auto p-2 3xl:p-4 h-[180px] md:h-[200px] lg:h-[260px] 3xl:h-[500px]">
        <div className="flex flex-col space-y-2">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex w-full my-2 ${
                msg.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {/* Message bubble with role label inside */}
              <div
                className={`p-3 max-w-[75%] rounded-lg whitespace-pre-wrap backdrop-blur-sm ${
                  msg.role === "user"
                    ? "bg-blue-100/80 text-gray-800 dark:bg-blue-600/70 dark:text-white border border-blue-200/50 dark:border-blue-500/30"
                    : msg.role === "associate"
                      ? "bg-white/60 dark:bg-gray-700/60 text-gray-800 dark:text-gray-200 border border-orange-200/50 dark:border-gray-600/30"
                      : "bg-white/60 dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 border border-orange-200/50 dark:border-gray-700/30"
                }`}
              >
                <div className="font-medium mb-1">
                  {msg.role === "user"
                    ? "You:"
                    : msg.role === "associate"
                    ? "Associate:"
                    : "CoachAI:"}
                </div>
                {msg.content}
                {streaming && i === messages.length - 1 && msg.role !== "user" && (
                  <span className="inline-block ml-1 animate-pulse">▌</span>
                )}
              </div>
            </div>
          ))}
          
          {/* LOADING INDICATOR - only show when loading and not streaming */}
          {loading && !streaming && (
            <div className="flex justify-start">
              <div className="p-4 bg-white/60 text-gray-900 dark:bg-gray-800/60 dark:text-gray-200 rounded-lg animate-pulse flex items-center gap-2 backdrop-blur-sm border border-orange-200/50 dark:border-gray-700/30">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                {loadingMessage || "Thinking..."}
              </div>
            </div>
          )}
          
          {/* Auto-scroll anchor at the end of messages */}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

    </div>
  );
};

export default ChatInterface;
